import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Alert,
  Row,
  Col,
  message,
} from "antd";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

interface ReferenceData {
  transportTypes: Array<{ id: number; name: string; abbreviation: string }>;
  bookingTypes: Array<{ id: number; name: string }>;
  containerSizes: Array<{ id: number; name: string }>;
  incoterms: Array<{ id: number; name: string }>;
  originPorts: Array<{ id: number; name: string; country: string }>;
  destinationPorts: Array<{ id: number; name: string; country: string }>;
}

interface RuleFormData {
  name: string;
  description?: string;
  valid_from: string;
  valid_to?: string;
  transport_type?: number;
  origin_port?: number;
  destination_port?: number;
  container_size?: number;
  incoterm?: number;
  shipment_type?: number;
  priority: number;
  is_active: boolean;
}

interface AddRuleModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (ruleData: RuleFormData) => Promise<void>;
  referenceData: ReferenceData;
  checkForDuplicates: (rule: Partial<RuleFormData>) => string | null;
}

export function AddRuleModal({
  visible,
  onCancel,
  onSubmit,
  referenceData,
  checkForDuplicates,
}: AddRuleModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [selectedTransportType, setSelectedTransportType] = useState<
    number | undefined
  >();

  // Generate rule name based on selected criteria
  const generateRuleName = (values: Partial<RuleFormData>) => {
    const parts: string[] = [];

    if (values.transport_type) {
      const transportType = referenceData.transportTypes.find(
        (t) => t.id === values.transport_type
      );
      if (transportType) parts.push(transportType.name);
    }

    // Only add booking type if seafreight is selected
    if (values.transport_type === 2 && values.shipment_type) {
      const bookingType = referenceData.bookingTypes.find(
        (t) => t.id === values.shipment_type
      );
      if (bookingType) parts.push(bookingType.name);
    }

    // Only add container size if seafreight is selected
    if (values.transport_type === 2 && values.container_size) {
      const containerSize = referenceData.containerSizes.find(
        (c) => c.id === values.container_size
      );
      if (containerSize) parts.push(containerSize.name);
    }

    if (values.incoterm) {
      const incoterm = referenceData.incoterms.find(
        (i) => i.id === values.incoterm
      );
      if (incoterm) parts.push(incoterm.name);
    }

    if (values.origin_port) {
      const originPort = referenceData.originPorts.find(
        (p) => p.id === values.origin_port
      );
      if (originPort) parts.push(originPort.name);
    }

    if (values.destination_port) {
      const destinationPort = referenceData.destinationPorts.find(
        (p) => p.id === values.destination_port
      );
      if (destinationPort) parts.push(`to ${destinationPort.name}`);
    }

    return parts.length > 0 ? parts.join(" - ") : "New Rule";
  };

  // Check for duplicates when form values change
  const handleFormChange = () => {
    const values = form.getFieldsValue() as Partial<RuleFormData>;
    const duplicateMessage = checkForDuplicates(values);
    setDuplicateError(duplicateMessage);

    // Auto-generate rule name
    const generatedName = generateRuleName(values);
    form.setFieldValue("name", generatedName);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = (await form.validateFields()) as RuleFormData;

      // Final duplicate check
      const duplicateMessage = checkForDuplicates(values);
      if (duplicateMessage) {
        message.error(duplicateMessage);
        return;
      }

      // Clear fields that don't apply to airfreight
      if (values.transport_type === 1) {
        // Airfreight
        values.shipment_type = undefined;
        values.container_size = undefined;
      }

      await onSubmit(values);
      form.resetFields();
      setDuplicateError(null);
      setSelectedTransportType(undefined);
    } catch (error) {
      console.error("Form validation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setDuplicateError(null);
    setSelectedTransportType(undefined);
    onCancel();
  };

  // Handle transport type change
  const handleTransportTypeChange = (value: number) => {
    setSelectedTransportType(value);

    // Clear booking type and container size if switching to airfreight
    if (value === 1) {
      // Airfreight
      form.setFieldsValue({
        shipment_type: undefined,
        container_size: undefined,
      });
    }

    handleFormChange();
  };

  useEffect(() => {
    if (visible) {
      // Set default values
      form.setFieldsValue({
        valid_from: dayjs(),
        is_active: true,
        priority: 1,
      });
    }
  }, [visible, form]);

  return (
    <Modal
      title="Add New Pricing Rule"
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={800}
      okButtonProps={{ disabled: !!duplicateError }}
    >
      {duplicateError && (
        <Alert
          message="Duplicate Rule Detected"
          description={duplicateError}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Alert
        message="Rule Hierarchy"
        description="Create rules at different levels: Transport Type → Booking Type (seafreight only) → Incoterm → Origin Port → Destination Port. Each level inherits from its parent."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Rule Name"
              name="name"
              rules={[{ required: true, message: "Please enter rule name" }]}
            >
              <Input placeholder="Auto-generated based on criteria" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Transport Type" name="transport_type">
              <Select
                placeholder="Select transport type"
                onChange={handleTransportTypeChange}
                allowClear
              >
                {referenceData.transportTypes.map((type) => (
                  <Option key={type.id} value={type.id}>
                    {type.name} ({type.abbreviation})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Booking Type"
              name="shipment_type"
              tooltip={
                selectedTransportType === 1
                  ? "Not applicable for airfreight"
                  : "Only applies to seafreight"
              }
            >
              <Select
                placeholder="Select booking type"
                disabled={selectedTransportType === 1} // Disable for airfreight
                allowClear
              >
                {referenceData.bookingTypes.map((type) => (
                  <Option key={type.id} value={type.id}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Container Size"
              name="container_size"
              tooltip={
                selectedTransportType === 1
                  ? "Not applicable for airfreight"
                  : "Only applies to seafreight"
              }
            >
              <Select
                placeholder="Select container size"
                disabled={selectedTransportType === 1} // Disable for airfreight
                allowClear
              >
                {referenceData.containerSizes.map((size) => (
                  <Option key={size.id} value={size.id}>
                    {size.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Incoterm" name="incoterm">
              <Select placeholder="Select incoterm" allowClear>
                {referenceData.incoterms.map((incoterm) => (
                  <Option key={incoterm.id} value={incoterm.id}>
                    {incoterm.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Origin Port" name="origin_port">
              <Select placeholder="Select origin port" allowClear>
                {referenceData.originPorts.map((port) => (
                  <Option key={port.id} value={port.id}>
                    {port.name}, {port.country}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Destination Port" name="destination_port">
              <Select placeholder="Select destination port" allowClear>
                {referenceData.destinationPorts.map((port) => (
                  <Option key={port.id} value={port.id}>
                    {port.name}, {port.country}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Valid From"
              name="valid_from"
              rules={[
                { required: true, message: "Please select valid from date" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Valid To" name="valid_to">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Priority"
              name="priority"
              rules={[{ required: true, message: "Please enter priority" }]}
            >
              <Input type="number" min={1} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Description" name="description">
          <TextArea rows={3} placeholder="Optional description for this rule" />
        </Form.Item>

        <Form.Item label="Active" name="is_active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
